import { useEffect, useState } from 'react';
import { useCategory } from '../../contexts';
import type { TagPill } from '../../schemas/api/search/category-tags';

/**
 * The tag pills for a category group, or null while they load. Keyed on the
 * group id the page derives from the URL, so a client-side move to another
 * group refetches (on mobile there are no fresh server props to carry them).
 * "Loading" and "none" are different answers: the row reserves its space and
 * draws placeholders for the first, and keeps the space empty for the second.
 */
export function useCategoryTagPills(groupId: number): TagPill[] | null {
  const { getTagPills } = useCategory();
  const [pills, setPills] = useState<{ groupId: number; pills: TagPill[] }>({ groupId: 0, pills: [] });

  useEffect(() => {
    let cancelled = false;
    void getTagPills(groupId).then(next => {
      if (!cancelled) setPills({ groupId, pills: next });
    });
    return () => {
      cancelled = true;
    };
  }, [groupId, getTagPills]);

  if (groupId <= 0) return [];
  // Pills from the group the page just left are never shown under the new one.
  return pills.groupId === groupId ? pills.pills : null;
}
